/**
 * כפתור "שלח ללקוח בוואטסאפ" — TASKS 5.3 · PRD 5.8.
 *
 * ⚠️ **מגבלה טכנית שחייבת להיות מתוכננת ב-UI:** קישור `wa.me` יכול למלא
 *    **טקסט בלבד** — ⛔ אי אפשר להצמיד קובץ דרך URL. לכן שתי זרימות:
 *
 *    - **במובייל (העיקרי):** Web Share API (`navigator.share({ files })`) —
 *      נתמך ב-Android Chrome וב-iOS Safari; טל בוחר וואטסאפ → את הלקוח → שלח.
 *      פעולה אחת אמיתית.
 *    - **fallback בדסקטופ / דפדפן ללא תמיכה:** שני כפתורים צמודים — "הורד
 *      PDF" + "פתח שיחה עם הודעה מוכנה"; טל גורר את הקובץ לשיחה בעצמו.
 *
 * ⭐ הקריאה לשרת (`sendQuoteWhatsappManual`) קורית **פעם אחת בלבד**, לפני
 *    הבחירה בין שתי הזרימות — היא זו שמתעדת ב-`whatsapp_deliveries` ובונה
 *    את קישור ה-click-to-chat. שתי הזרימות הן רק "מה עושים עם התוצאה".
 *
 * 🔴 **באג אמיתי שנתפס ב-QA בדפדפן אמיתי (Playwright MCP):** התיעוד ב-DB
 *    נכתב מיד עם תשובת השרת, אבל מסך פרטי ההצעה המשיך להציג "עדיין לא נשלחה
 *    בוואטסאפ ידנית" — כי שום דבר לא ריענן את `whatsappDeliveries` אחרי
 *    השליחה. ⭐ תוקן: `onSent` נקרא **מיד אחרי תשובת השרת**, ⛔ לא אחרי
 *    שהשיתוף עצמו הסתיים — כי הרשומה כבר נכתבה בנקודה הזו בלי קשר לתוצאת
 *    ה-Web Share.
 */
import { useState } from 'react';
import { WhatsappLogo } from '@phosphor-icons/react';
import { isAdminApiError } from './adminApi.js';
import { sendQuoteWhatsappManual } from './quotesApi.js';
import { StatusMessage } from './StatusMessage.js';

interface FallbackLinks {
  clickToChatUrl: string;
  pdfUrl: string;
}

interface WhatsAppSendButtonProps {
  quoteId: number;
  quoteNumber: string;
  className?: string;
  /** ⭐ נקרא מיד אחרי שהשרת אישר את השליחה (תיעד ב-whatsapp_deliveries) */
  onSent?: () => void;
}

export function WhatsAppSendButton({
  quoteId,
  quoteNumber,
  className,
  onSent,
}: WhatsAppSendButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fallback, setFallback] = useState<FallbackLinks | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);

  async function handleClick() {
    setError(null);
    setFallback(null);
    setBusy(true);
    try {
      const { clickToChatUrl, pdfUrl, pdfFilename } = await sendQuoteWhatsappManual(quoteId);
      onSent?.();

      let shared = false;

      if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
        try {
          const pdfRes = await fetch(pdfUrl, { credentials: 'same-origin' });
          const blob = await pdfRes.blob();
          const file = new File([blob], pdfFilename, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text: `הצעת מחיר ${quoteNumber}` });
            shared = true;
          }
        } catch (shareErr) {
          // ⛔ ⛔ ביטול משתמש (לחיצה על "ביטול" בגיליון השיתוף) ⛔ אינו שגיאה —
          //    לא מציגים fallback במקרה הזה, טל פשוט התחרט.
          if (shareErr instanceof DOMException && shareErr.name === 'AbortError') {
            shared = true;
          }
        }
      }

      if (!shared) {
        setFallback({ clickToChatUrl, pdfUrl });
      } else {
        setSentAt(Date.now());
      }
    } catch (err) {
      setError(isAdminApiError(err) ? err.message : 'שגיאה בהכנת השליחה בוואטסאפ.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={className}>
      <button
        type="button"
        className="btn btn-secondary"
        disabled={busy}
        onClick={() => void handleClick()}
      >
        <WhatsappLogo size={16} weight="regular" aria-hidden="true" />
        {busy ? 'מכין…' : 'שלח ללקוח בוואטסאפ'}
      </button>

      {sentAt !== null && (
        <StatusMessage className="mt-1 text-sm">נפתח שיתוף בוואטסאפ.</StatusMessage>
      )}

      {error && (
        <p role="alert" className="text-danger mt-1 text-sm">
          {error}
        </p>
      )}

      {fallback && (
        <span className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <a href={fallback.pdfUrl} download className="rounded border border-line px-2 py-1">
            הורד PDF
          </a>
          <a
            href={fallback.clickToChatUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded border border-line px-2 py-1"
          >
            פתח שיחה עם הודעה מוכנה
          </a>
        </span>
      )}
    </span>
  );
}
